/*
 * Display.jsx
 * -----------
 * Full screen smart mirror display mode.
 * Public route -- authenticated via token in URL query param.
 * Validates token against hashed version stored in Cosmos.
 * Shows Today and Upcoming items in large readable text.
 * Auto refreshes every 5 minutes.
 * Always dark theme regardless of user preference.
 * Supports compact, comfortable, and large layouts.
 */

import React, { useState, useEffect, useCallback } from "react";

// ============================================
// CLOCK
// Updates every second
// ============================================
function Clock() {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const timeString = time.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });

  const dateString = time.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric"
  });

  return (
    <div style={{ textAlign: "center", marginBottom: "var(--space-8)" }}>
      <div style={{
        fontFamily: "var(--font-display)",
        fontWeight: 900,
        fontSize: "clamp(64px, 12vw, 120px)",
        color: "var(--accent-green)",
        lineHeight: 1,
        letterSpacing: "-3px"
      }}>
        {timeString}
      </div>
      <div style={{
        fontFamily: "var(--font-body)",
        fontSize: "clamp(16px, 2vw, 24px)",
        color: "var(--text-muted)",
        marginTop: "var(--space-2)"
      }}>
        {dateString}
      </div>
    </div>
  );
}

// ============================================
// ITEM ROW
// Single checklist item in display mode
// ============================================
function DisplayItem({ item, layout }) {
  const fontSizes = {
    compact:     { text: "16px", meta: "12px" },
    comfortable: { text: "20px", meta: "14px" },
    large:       { text: "26px", meta: "16px" }
  };

  const sizes = fontSizes[layout] || fontSizes.comfortable;

  const priorityColors = {
    high:   "var(--accent-gold)",
    medium: "var(--accent-green)",
    low:    "var(--text-muted)"
  };

  const isOverdue = item.dueDate && new Date(item.dueDate) < new Date();
  const isToday = (() => {
    if (!item.dueDate) return false;
    const due = new Date(item.dueDate);
    const now = new Date();
    return due.toDateString() === now.toDateString();
  })();

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "var(--space-4)",
      padding: layout === "compact" ? "var(--space-2) 0" : "var(--space-3) 0",
      borderBottom: "1px solid var(--border)"
    }}>
      {/* Priority dot */}
      <div style={{
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        background: priorityColors[item.priority] || "var(--text-muted)",
        flexShrink: 0
      }} />

      {/* Text */}
      <span style={{
        fontFamily: "var(--font-body)",
        fontSize: sizes.text,
        fontWeight: 500,
        color: isOverdue ? "var(--status-overdue)" : "var(--text-primary)",
        flex: 1
      }}>
        {item.text}
      </span>

      {/* Due date */}
      {item.dueDate && (
        <span style={{
          fontFamily: "var(--font-body)",
          fontSize: sizes.meta,
          color: isOverdue
            ? "var(--status-overdue)"
            : isToday
              ? "var(--accent-green)"
              : "var(--text-muted)",
          fontWeight: isToday || isOverdue ? 600 : 400,
          flexShrink: 0
        }}>
          {isToday ? "Today" : isOverdue
            ? "Overdue"
            : new Date(item.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
        </span>
      )}
    </div>
  );
}

// ============================================
// DISPLAY SECTION
// Today or Upcoming group
// ============================================
function DisplaySection({ title, items, layout, color }) {
  if (items.length === 0) return null;

  const titleSizes = {
    compact:     "16px",
    comfortable: "20px",
    large:       "26px"
  };

  return (
    <div style={{ marginBottom: "var(--space-7)" }}>
      <h2 style={{
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: titleSizes[layout] || "20px",
        color,
        margin: "0 0 var(--space-3)",
        letterSpacing: "-0.5px",
        borderBottom: "2px solid " + color,
        paddingBottom: "var(--space-2)"
      }}>
        {title}
        <span style={{
          fontFamily: "var(--font-body)",
          fontSize: "14px",
          fontWeight: 400,
          color: "var(--text-muted)",
          marginLeft: "var(--space-3)"
        }}>
          {items.length} item{items.length !== 1 ? "s" : ""}
        </span>
      </h2>
      {items.map(item => (
        <DisplayItem key={item.id} item={item} layout={layout} />
      ))}
    </div>
  );
}

// ============================================
// STREAK DISPLAY
// Shows active streaks at the bottom
// ============================================
function StreakDisplay({ checklists, layout }) {
  const active = checklists.filter(c => c.streak > 0);
  if (active.length === 0) return null;

  return (
    <div style={{
      position: "fixed",
      bottom: "var(--space-6)",
      left: "50%",
      transform: "translateX(-50%)",
      display: "flex",
      gap: "var(--space-4)",
      flexWrap: "wrap",
      justifyContent: "center"
    }}>
      {active.map(checklist => (
        <div key={checklist.id} style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-2)",
          background: "var(--accent-gold-muted)",
          border: "1px solid var(--accent-gold)",
          borderRadius: "100px",
          padding: "var(--space-1) var(--space-3)"
        }}>
          <span style={{ fontSize: layout === "large" ? "18px" : "14px" }}>🔥</span>
          <span style={{
            fontFamily: "var(--font-body)",
            fontSize: layout === "large" ? "16px" : "13px",
            fontWeight: 600,
            color: "var(--accent-gold)"
          }}>
            {checklist.streak}
          </span>
          <span style={{
            fontFamily: "var(--font-body)",
            fontSize: layout === "large" ? "14px" : "12px",
            color: "var(--text-muted)"
          }}>
            {checklist.title}
          </span>
        </div>
      ))}
    </div>
  );
}

// ============================================
// ERROR STATE
// Shown when token is invalid or missing
// ============================================
function ErrorState({ message }) {
  return (
    <div style={{
      minHeight: "100vh",
      background: "#0A0A08",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "var(--space-4)",
      padding: "var(--space-6)"
    }}>
      <span style={{ fontSize: "48px" }}>🔒</span>
      <h1 style={{
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: "28px",
        color: "#F0F0E8",
        margin: 0,
        textAlign: "center"
      }}>
        Access Denied
      </h1>
      <p style={{
        fontFamily: "var(--font-body)",
        fontSize: "16px",
        color: "#6C6C5E",
        margin: 0,
        textAlign: "center",
        maxWidth: "400px"
      }}>
        {message || "This display link is invalid or has been revoked. Generate a new one in Settings."}
      </p>
    </div>
  );
}

// ============================================
// MAIN EXPORT
// ============================================
export default function Display() {
  const [status, setStatus] = useState("loading");
  const [checklists, setChecklists] = useState([]);
  const [layout, setLayout] = useState(
    localStorage.getItem("mirrorLayout") || "comfortable"
  );

  // Force mirror theme
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "mirror");
    return () => {
      const saved = localStorage.getItem("theme") || "light";
      document.documentElement.setAttribute("data-theme", saved);
    };
  }, []);

  // ============================================
  // VALIDATE TOKEN + FETCH DATA
  // ============================================
  const loadData = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const rawToken = params.get("token");

    if (!rawToken) {
      setStatus("error");
      return;
    }

    try {
      // Hash the token from the URL
      const encoder = new TextEncoder();
      const data = encoder.encode(rawToken);
      const hashBuffer = await crypto.subtle.digest("SHA-256", data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashedToken = hashArray.map(b => b.toString(16).padStart(2, "0")).join("");

      // Send hashed token to API for validation
      const res = await fetch("/api/checklists?mirrorToken=" + hashedToken, {
        headers: { "Content-Type": "application/json" }
      });

      if (res.status === 401 || res.status === 403) {
        setStatus("error");
        return;
      }

      const data2 = await res.json();
      setChecklists(data2);
      setStatus("ok");
    } catch (err) {
      console.error("Display load error:", err);
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    loadData();
    // Auto refresh every 5 minutes
    const interval = setInterval(loadData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadData]);

  // ============================================
  // DERIVE TODAY + UPCOMING ITEMS
  // ============================================
  const now = new Date();
  const todayStart = new Date(now); todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date(now); todayEnd.setHours(23, 59, 59, 999);
  const next7Days = new Date(now); next7Days.setDate(now.getDate() + 7);

  const todayItems = [];
  const upcomingItems = [];

  for (const checklist of checklists) {
    for (const item of checklist.items || []) {
      if (item.checked || !item.dueDate) continue;
      const due = new Date(item.dueDate);
      const enriched = { ...item, checklistTitle: checklist.title };
      if (due >= todayStart && due <= todayEnd) todayItems.push(enriched);
      else if (due > todayEnd && due <= next7Days) upcomingItems.push(enriched);
    }
  }

  // Sort by priority within each section
  const byPriority = (a, b) => {
    const w = { high: 0, medium: 1, low: 2 };
    return (w[a.priority] ?? 2) - (w[b.priority] ?? 2);
  };
  todayItems.sort(byPriority);
  upcomingItems.sort(byPriority);

  if (status === "loading") {
    return (
      <div style={{
        minHeight: "100vh",
        background: "#0A0A08",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
        <p style={{ fontFamily: "sans-serif", color: "#5C5C4E", fontSize: "16px" }}>
          Loading...
        </p>
      </div>
    );
  }

  if (status === "error") {
    return <ErrorState />;
  }

  const maxWidth = layout === "large" ? "900px" : layout === "compact" ? "600px" : "740px";

  return (
    <div style={{
      minHeight: "100vh",
      background: "var(--bg-primary)",
      padding: "var(--space-8) var(--space-6)",
      overflowY: "auto"
    }}>
      <div style={{
        maxWidth,
        margin: "0 auto"
      }}>

        {/* Clock */}
        <Clock />

        {/* Checklist sections */}
        <DisplaySection
          title="Today"
          items={todayItems}
          layout={layout}
          color="var(--accent-green)"
        />
        <DisplaySection
          title="Upcoming"
          items={upcomingItems}
          layout={layout}
          color="var(--accent-gold)"
        />

        {/* Empty state */}
        {todayItems.length === 0 && upcomingItems.length === 0 && (
          <div style={{
            textAlign: "center",
            padding: "var(--space-8) 0"
          }}>
            <p style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(20px, 3vw, 32px)",
              color: "var(--text-muted)",
              margin: 0
            }}>
              Nothing due today or this week.
            </p>
          </div>
        )}
      </div>

      {/* Streak display */}
      <StreakDisplay checklists={checklists} layout={layout} />
    </div>
  );
}
